import {availabilityService} from "@/modules/availability/services/availabilityService";
import moment from "moment";
import {convertTime12to24, convertTime24To12, mapBetweenDates, iterateBetweenDates} from "@/modules/common/services/timeService";
import { apiCacheService, apiCacheEnhancedService } from "@/modules/common/services";

const TIME_REGEX = /^(\d\d):(\d\d)$/;
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
let SLOTS_TO_MILLISECOND_MAP = {};

function getSlotsMapFromCache(availability_id) {
  if (!SLOTS_TO_MILLISECOND_MAP[availability_id]) {
    const cacheKey = `SLOTS_TO_MILLISECOND_MAP_${availability_id}`;
    let slotsCacheText = localStorage.getItem(cacheKey)
    const slotsCacheVersion = localStorage.getItem(`${cacheKey}_VERSION`)
    const currSlotsCacheVersion = new Date().getDate().toString()
    if (currSlotsCacheVersion !== slotsCacheVersion) {
      localStorage.removeItem(cacheKey)
      localStorage.setItem(`${cacheKey}_VERSION`, currSlotsCacheVersion)
      slotsCacheText = null
    }
    SLOTS_TO_MILLISECOND_MAP[availability_id] = JSON.parse(slotsCacheText) || {}
  }
  return SLOTS_TO_MILLISECOND_MAP[availability_id];
}

function saveSlotsMapInCache(slotsMap, availability_id) {
  try {
    localStorage.setItem(`SLOTS_TO_MILLISECOND_MAP_${availability_id}`, JSON.stringify(slotsMap));
  } catch {
    localStorage.removeItem(`SLOTS_TO_MILLISECOND_MAP_${availability_id}`)
  }
}

const slotsMapToMilliseconds = (slotsMap) => {
  let res = Object.assign({}, ...Object.values(slotsMap).flatMap(val => Object.values(val)) )
  res = new Map(Object.entries(res).map(([key, val]) => [parseInt(key), val]))
  return res
}

const modifySlotsToCorrectAmPmMode = (slots, isAmPmTimeFormat) => {
  return slots.forEach((slot) => {
    slot.start_time_24 = slot.start_time
    slot.end_time_24 = slot.end_time
    if(isAmPmTimeFormat) {
      slot.start_time = convertTime24To12(slot.start_time)
      slot.end_time = convertTime24To12(slot.end_time)
    } else {
      slot.start_time = convertTime12to24(slot.start_time)
      slot.end_time = convertTime12to24(slot.end_time)
    }
  })
}

const mergeSlots = (slots) => {
  return slots.reduce((arr, curr) => {
    const lastSlotEndTime = arr[arr.length - 1]?.end_time_24
    const currSlotStartTime = curr.start_time_24
    const areSlotsConecutive = lastSlotEndTime === currSlotStartTime
    if (areSlotsConecutive) {
      arr[arr.length - 1].end_time_24 = curr.end_time_24;
    } else {
      arr.push({
        start_time_24: curr.start_time_24,
        end_time_24: curr.end_time_24,
      });
    }
    return arr;
  }, []);
}

const modifySlotsWithExceptions = (dateSlot, weeklyException, dateString, timeZone) => {
  const weeklyIgnore = weeklyException.ignore[dateString];
  if(weeklyIgnore) {
    dateSlot.slots = dateSlot.slots.filter(slot => {

      if(weeklyIgnore[slot.id]) {
        dateSlot.isEdited = true
        dateSlot.ignoreSlots.push(weeklyIgnore[slot.id])
      }
      return !weeklyIgnore[slot.id]
    })
  }
  const weeklyExceptions = weeklyException.exceptions[dateString]
  if(weeklyExceptions) {
    dateSlot.slots = dateSlot.slots.concat(weeklyExceptions.map(slot => {
        let start_time = moment(slot.startObj).tz(timeZone).format('HH:mm');
        let end_time = moment(slot.endObj).tz(timeZone).format('HH:mm');
        return {
          startObj: slot.startObj.clone(),
          endObj: slot.endObj.clone(),
          start_time: start_time,
          end_time: end_time,
          uid: slot.uid,
          weekly_availability_uid: slot.weekly_availability_uid
        }
      })
    )
    dateSlot.isEdited = true
  }
}

const isThereDifferenceInExceptionInRange = (currWeeklyException, newWeeklyException, startTime, endTime) => {
  return mapBetweenDates(startTime, endTime).some(currDate => {
    for (const uid in newWeeklyException) {
      const currOldException = currWeeklyException[uid]?.exceptions?.[currDate]
      const currNewException = newWeeklyException[uid]?.exceptions?.[currDate]
      const currOldIgnore = currWeeklyException[uid]?.ignore?.[currDate]
      const currNewIgnore = newWeeklyException[uid]?.ignore?.[currDate]
      const shouldAssignNewException = JSON.stringify(currOldException) !== JSON.stringify(currNewException)
      const shouldAssignNewIgnore = JSON.stringify(currOldIgnore) !== JSON.stringify(currNewIgnore)
      if(shouldAssignNewException || shouldAssignNewIgnore) {
        return true
      }
    }

  })
}

const assignExceptionInRange = (currentWeeklyException, newWeeklyException, startTime, endTime) => {
  const dateRangeArray = mapBetweenDates(startTime, endTime)
  const weeklyExceptionCopy = {...currentWeeklyException}
  dateRangeArray.forEach((currDate) => {
    for (const uid in newWeeklyException) {
      weeklyExceptionCopy[uid] = weeklyExceptionCopy[uid] || {exceptions: {}, ignore: {}}
      const currException = newWeeklyException[uid]
      weeklyExceptionCopy[uid].exceptions[currDate] = currException.exceptions[currDate]
      weeklyExceptionCopy[uid].ignore[currDate] = currException.ignore[currDate]
    }
  })
  return weeklyExceptionCopy
}

const parseTimeStrToMilliseconds = (timeStr) =>{
  const [, hour, minute] = (timeStr).match(TIME_REGEX);
  return hour * 60 * MS_PER_MINUTE + minute * MS_PER_MINUTE;
}

const getSlotStartAndEndTimes = (dayStartMS, slot, calendarIntervalMS) => {
  let startTimeMS = dayStartMS + parseTimeStrToMilliseconds(slot.start_time_24);
  let endTimeMS = dayStartMS + parseTimeStrToMilliseconds(slot.end_time_24);

  if (endTimeMS - startTimeMS >= calendarIntervalMS) {
    endTimeMS -= calendarIntervalMS;
  }

  if(endTimeMS - startTimeMS < calendarIntervalMS) {
    // on an availability with a duration smaller then calendarIntervalMS. startTime needs to be set to the start of the cell
    const [, , minute] = (slot.start_time_24).match(TIME_REGEX);
    const minuteMs = minute*MS_PER_MINUTE;
    startTimeMS -= minuteMs;
  }

  const isDayStartDst = moment(dayStartMS).isDST();
  const isSlotStartDst = moment(startTimeMS).isDST();
  const isSlotEndDst = moment(endTimeMS).isDST();

  if (isDayStartDst) {
    if (!isSlotStartDst) {
      startTimeMS += MS_PER_HOUR
    }
    if (!isSlotEndDst) {
      endTimeMS += MS_PER_HOUR
    }
  } else {
    if (isSlotStartDst) {
      startTimeMS -= MS_PER_HOUR
    }
    if (isSlotEndDst) {
      endTimeMS -= MS_PER_HOUR
    }
  }
  const millisecondsToRound = startTimeMS % calendarIntervalMS;
  if (millisecondsToRound) {
    startTimeMS += calendarIntervalMS - millisecondsToRound;
  }
  return {
    startTimeMS,
    endTimeMS,
  }
}

const state = {
  currFetchIds: {
    weekly: null,
    exception: null,
  },
  weeklyAvailability: {},
  currWeeklyAvailabilityUids: null,
  isGeneralAvailability: false,
  weeklyException: {},
  lastLoadedExceptionRange: {startTime: null, endTime: null},
  didFinishFetchingWeeklyAvailability: true,
  didFinishFetchingWeeklyAvailabilityExceptions: true,

};

const mutations = {
  toggleDidFetch(state, {type, value}) {
    if(type === 'weeklyAvailability') {
      state.didFinishFetchingWeeklyAvailability = value
    } else {
      state.didFinishFetchingWeeklyAvailabilityExceptions = value
    }
  },
  setCurrFetchIds(state, currFetchIds) {
    state.currFetchIds = currFetchIds
  },
  setWeeklyAvailability(state, weeklyAvailability) {
    const modifiedData = {}
    for (const weeklyAvailabilityUid in weeklyAvailability) {
      const newWeeklyAvailability = weeklyAvailability[weeklyAvailabilityUid].map(slot => {
        const slotCopy = {...slot}
        slot.slots = slot.slots.sort((a, b) => a.start_time < b.start_time ? -1 : 1)
        return slotCopy
      }).sort((a, b) => a.day - b.day)
      if (JSON.stringify(state.weeklyAvailability[weeklyAvailabilityUid]) !== JSON.stringify(newWeeklyAvailability)) {
        modifiedData[weeklyAvailabilityUid] = newWeeklyAvailability
      }
    }
    if (Object.values(modifiedData).length) {
      state.weeklyAvailability = {
        ...state.weeklyAvailability,
        ...modifiedData
      };
    }
  },
  setCurrWeeklyAvailabilityUids(state, {currWeeklyAvailabilityUids, general_availability}) {
    state.currWeeklyAvailabilityUids = currWeeklyAvailabilityUids
    state.isGeneralAvailability = general_availability
    state.weeklyAvailability = {}
    state.weeklyException = {}
  },
  setWeeklyExceptionCache(state, {cachedWeeklyAvailabilityException, timeZone}) {

    for(const uid in cachedWeeklyAvailabilityException) {
      for(const date in cachedWeeklyAvailabilityException[uid].exceptions) {
        if(cachedWeeklyAvailabilityException[uid].exceptions[date]) {
          cachedWeeklyAvailabilityException[uid].exceptions[date] = cachedWeeklyAvailabilityException[uid].exceptions[date].map(slot => {
            return {
              ...slot,
              startObj: moment(slot.startObj).tz(timeZone),
              endObj: moment(slot.endObj).tz(timeZone),
            }
          })
        }
      }
    }
    state.weeklyException = cachedWeeklyAvailabilityException
  },
  setWeeklyException(state, {newWeeklyException, dateRange, weeklyAvailabilityUid}) {

    const shouldModify = isThereDifferenceInExceptionInRange(state.weeklyException, newWeeklyException, dateRange.startTime, dateRange.endTime)
    if(!shouldModify) {
      return
    }
    state.weeklyException = assignExceptionInRange(state.weeklyException, newWeeklyException, dateRange.startTime, dateRange.endTime)
    localStorage.setItem(`weeklyAvailabilityException-${weeklyAvailabilityUid}`, JSON.stringify(state.weeklyException))
  },
  setLastLoadedExceptionRange(state, lastLoadedExceptionRange) {
    state.lastLoadedExceptionRange = lastLoadedExceptionRange
  },

};

const actions = {
  async setCurrWeeklyAvailabilityUids({commit}, {currWeeklyAvailabilityUids = null, general_availability = false}) {
    commit('setCurrWeeklyAvailabilityUids', {currWeeklyAvailabilityUids, general_availability})
  },
  async fetchWeeklyAvailability({commit, getters, rootGetters}, {currWeeklyAvailabilityUids = null, general_availability = true, fetchSelectedStaff = false, useSelectedStaffTZ = false}) {
    let res;
    if(!currWeeklyAvailabilityUids?.length) {
      currWeeklyAvailabilityUids = getters.currWeeklyAvailabilityUids || [rootGetters["accountModule/staff"]?.weekly_availability_uid]
    }
    if(getters.weeklyAvailabilityUids !== currWeeklyAvailabilityUids) {
      commit('setCurrWeeklyAvailabilityUids', {currWeeklyAvailabilityUids, general_availability})
    }
    let currFetchIdsCopy = JSON.parse(JSON.stringify(getters.currFetchIds))
    currFetchIdsCopy.weekly = Math.random()
    commit('setCurrFetchIds', currFetchIdsCopy)
    const isFetchingOnlyCurrStaffAvailability = currWeeklyAvailabilityUids.length === 1 && currWeeklyAvailabilityUids[0] === rootGetters["accountModule/staff"]?.weekly_availability_uid
    const fetchMethod = !general_availability || isFetchingOnlyCurrStaffAvailability || fetchSelectedStaff ? availabilityService.fetchWeeklyAvailability : availabilityService.fetchWeeklyAvailabilities
    const callFunc = async () => {
      const weeklyAvailabilities = await fetchMethod(isFetchingOnlyCurrStaffAvailability || fetchSelectedStaff ? getters.weeklyAvailabilityUids[0] : getters.weeklyAvailabilityUids, general_availability, useSelectedStaffTZ)
      const toCommit = !general_availability || isFetchingOnlyCurrStaffAvailability || fetchSelectedStaff ? {[currWeeklyAvailabilityUids[0]]: weeklyAvailabilities} : weeklyAvailabilities
      if (getters.currFetchIds.weekly === currFetchIdsCopy.weekly) {
        commit('setWeeklyAvailability', toCommit)
        commit('toggleDidFetch', {type: 'weeklyAvailability', value: true})
      }
      return Object.entries(toCommit ?? {})
        .map(([weekly_availability_uid, weeklyAvailability]) => ({
          weekly_availability_uid,
          weeklyAvailability
        }));
    };
    const onCache = (cachedWeeklyAvailabilities) => {
      commit('toggleDidFetch', {type: 'weeklyAvailability', value: !!cachedWeeklyAvailabilities.length})
      const availability = Object.fromEntries(
        cachedWeeklyAvailabilities.map(({
                                          weekly_availability_uid,
                                          weeklyAvailability
                                        }) => [weekly_availability_uid, weeklyAvailability])
      );
      commit('setWeeklyAvailability', availability)
    }
    if (rootGetters.hasFeature('local_storage_enhancement')) {
      await apiCacheEnhancedService.handle('WEEKLY_AVAILABILITY', ['weekly_availability_uid'], currWeeklyAvailabilityUids, callFunc, onCache)
    } else {
      await apiCacheService.get({
        key: `WEEKLY_AVAILABILITY_CACHE_KEY_${getters.weeklyAvailabilityUids}`,
        shouldLoadCache: !Object.keys(state.weeklyAvailability).length,
        callFunc,
        onSuccess: () => state.weeklyAvailability,
        onCache: (cachedWeeklyAvailabilities) => {
          commit('toggleDidFetch', {type: 'weeklyAvailability', value: !!cachedWeeklyAvailabilities})
          if (cachedWeeklyAvailabilities) {
            commit('setWeeklyAvailability', cachedWeeklyAvailabilities)
          }
        },
      });
    }
    return res
  },

  async updateWeeklyAvailability({commit, getters}, {start_time, end_time, day, id, useSelectedStaffTimeZone}) {
    try {
      const weekly_availability_uid = getters.weeklyAvailabilityUids[0];
      const weeklyAvailability = await availabilityService.setWeeklyAvailability(id, {
        start_time,
        end_time,
        day,
        weekly_availability_uid,
        use_selected_staff_time_zone: useSelectedStaffTimeZone
      });
      commit('setWeeklyAvailability', {[weekly_availability_uid]: weeklyAvailability})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async updateEnabledDay({commit, getters}, {enabled, day}) {
    try {
      const weeklyAvailability = await availabilityService.updateEnabledDay(getters.weeklyAvailabilityUids[0], {
        enabled,
        day
      });
      const weekly_availability_uid = getters.weeklyAvailabilityUids[0];
      commit('setWeeklyAvailability', {[weekly_availability_uid]: weeklyAvailability})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async addWeeklyAvailability({commit, getters}, {start_time, end_time, day, useSelectedStaffTimeZone}) {
    try {
      const weekly_availability_uid = getters.weeklyAvailabilityUids[0];
      const weeklyAvailability = await availabilityService.createWeeklyAvailability({
        weekly_availability_uid,
        start_time,
        end_time,
        day,
        use_selected_staff_time_zone: useSelectedStaffTimeZone
      });
      commit('setWeeklyAvailability', {[weekly_availability_uid]: weeklyAvailability})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async deleteWeeklyAvailabilitySlots({commit, getters}, {slotIds, useSelectedStaffTimeZone = false}) {
    try {
      const weeklyAvailability = await availabilityService.deleteWeeklyAvailabilitySlots(slotIds, useSelectedStaffTimeZone);
      const weekly_availability_uid = getters.weeklyAvailabilityUids[0];
      commit('setWeeklyAvailability', {[weekly_availability_uid]: weeklyAvailability})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },
  async fetchAvailabilityException({commit, getters, rootGetters}, {startTime, endTime, useSelectedStaffTZ = false, selectedStaffTZ = null}) {
    let currFetchIdsCopy = JSON.parse(JSON.stringify(getters.currFetchIds))
    let res;
    const timeZone = selectedStaffTZ || rootGetters["accountModule/staffTimeZoneName"];

    try {

      const cachedWeeklyAvailabilityException = JSON.parse(localStorage.getItem(`weeklyAvailabilityException-${getters.weeklyAvailabilityUids}`))
      commit('toggleDidFetch', {type: 'exception', value: !!cachedWeeklyAvailabilityException})
      if(cachedWeeklyAvailabilityException) {
        commit('setWeeklyExceptionCache', {cachedWeeklyAvailabilityException, timeZone})
        commit('toggleDidFetch', {type: 'exception', value: true})
      }
      currFetchIdsCopy.exception = Math.random()
      commit('setCurrFetchIds', currFetchIdsCopy)
      commit('toggleDidFetch', {type: 'exception', value: false})
      const lastLoadedExceptionRange = {startTime, endTime}
      commit('setLastLoadedExceptionRange', lastLoadedExceptionRange)
      const weeklyException = await availabilityService.fetchAvailabilityException(getters.weeklyAvailabilityUids, startTime, endTime, timeZone);
      commit('setWeeklyException', { newWeeklyException: weeklyException, dateRange: lastLoadedExceptionRange, weeklyAvailabilityUids: getters.weeklyAvailabilityUids })

    } catch (e) {
      console.error(e)
      res = Promise.reject(e)
    } finally {
      if(getters.currFetchIds.exception === currFetchIdsCopy.exception) {
        commit('toggleDidFetch', {type: 'exception', value: true})
      }
    }
    return res
  },
  async refreshAvailabilityException({getters, dispatch}, {selectedStaffTimeZone}) {
    try {
      const timeZone = {selectedStaffTZ: selectedStaffTimeZone}
      dispatch('fetchAvailabilityException', {...getters.lastLoadedExceptionRange, ...timeZone})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async createAvailabilitySlot({dispatch, getters}, {start_time, end_time, useSelectedStaffTimeZone = false, selectedStaffTimeZone = null}) {
    try {
      await availabilityService.createAvailabilitySlot({weekly_availability_uid: getters.weeklyAvailabilityUids[0], start_time, end_time, use_selected_staff_time_zone: useSelectedStaffTimeZone});
      await dispatch('refreshAvailabilityException', {selectedStaffTimeZone})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },
  async deleteAvailabilitySlot({dispatch, getters}, {timeSlotUid, timeSlotId, start_time, end_time, useSelectedStaffTimeZone = false, selectedStaffTimeZone = null}) {
    try {
      await availabilityService.deleteAvailabilitySlot({timeSlotUid, timeSlotId, weekly_availability_uid: getters.weeklyAvailabilityUids[0], start_time, end_time, useSelectedStaffTimeZone: useSelectedStaffTimeZone});

      await dispatch('refreshAvailabilityException', {selectedStaffTimeZone})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async editAvailabilitySlot({dispatch, getters}, {timeSlotUid, start_time, end_time, useSelectedStaffTZ = false, selectedStaffTimeZone = null}) {
    try {
      await availabilityService.editAvailabilitySlot({timeSlotUid, weekly_availability_uid: getters.weeklyAvailabilityUids[0], start_time, end_time, useSelectedStaffTimeZone: useSelectedStaffTZ});

      await dispatch('refreshAvailabilityException', {selectedStaffTimeZone})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  },

  async resetAvailability({dispatch, getters}, {slotsToDelete, slotsToUndoIgnore, selectedStaffTimeZone = null}) {
    try {
      const deletePromisesArray = Promise.all(slotsToDelete.map(slot => dispatch('deleteAvailabilitySlot', slot)))
      const undoSlotIgnorePromisesArray = Promise.all(slotsToUndoIgnore.map(slot => availabilityService.undoIgnoreWeeklySlot(slot)))
      await Promise.all([deletePromisesArray, undoSlotIgnorePromisesArray])
      await dispatch('refreshAvailabilityException', {selectedStaffTimeZone})
    } catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  }
}

const getters = {
  currFetchIds: state => state.currFetchIds,
  getWeeklyAvailability: (state, getters) => (weeklyAvailabilityUid = getters.weeklyAvailabilityUids[0]) =>
     state.weeklyAvailability[weeklyAvailabilityUid] || [],
  weeklyAvailabilityUids: (state, getters, rootState, rootGetters) => {
    return state.currWeeklyAvailabilityUids || [rootGetters["accountModule/staff"]?.weekly_availability_uid]
  },
  weeklyAvailability: state => state.weeklyAvailability,
  weeklyException: state => state.weeklyException,
  lastLoadedExceptionRange: state => state.lastLoadedExceptionRange,
  didFinishFetching: state => state.didFinishFetchingWeeklyAvailabilityExceptions && state.didFinishFetchingWeeklyAvailability,
  isGeneralAvailability: state => state.isGeneralAvailability,

  dateStringsArray: (state) => {
    return mapBetweenDates(state.lastLoadedExceptionRange.startTime, state.lastLoadedExceptionRange.endTime)
  },
  weeklyAvailabilityByDayMap: (state, getters) => {
    return getters.weeklyAvailabilityUids.reduce((acc, weeklyAvailabilityUid) => {
      acc[weeklyAvailabilityUid] = state.weeklyAvailability[weeklyAvailabilityUid].reduce((acc, currDay) => {
        acc[currDay.day] = currDay
        return acc
      },{})
      return acc
    }, {})
  },
  weeklyAvailabilityByDateStringMap: (state, getters, rootState, rootGetters ) => {
    const dateStringsArray = getters.dateStringsArray
    return getters.weeklyAvailabilityUids.reduce((acc, weeklyAvailabilityUid) => {
      const weeklyAvailabilityByDayMap = getters.weeklyAvailabilityByDayMap[weeklyAvailabilityUid]
      acc[weeklyAvailabilityUid] = dateStringsArray.reduce((acc, dateString) => {
        let availabilityDate = moment(dateString)
        const slotDay = weeklyAvailabilityByDayMap[availabilityDate.days()]
        let slots = slotDay && slotDay.enabled ? slotDay.slots : []
        acc[dateString] = {
          availabilityDate,
          slots,
          isEdited: false,
        }
        return acc
      }, {})
      return acc
    }, {})

  },
  availabilitiesWithExceptions: (state, getters, rootState, rootGetters) => {
    const data = {}
    for (const weeklyAvailabilityUid of state.currWeeklyAvailabilityUids) {
      const weeklyException = state.weeklyException[weeklyAvailabilityUid]
      if (!state.weeklyAvailability[weeklyAvailabilityUid]?.length) {
        continue
      }
      const weeklyAvailabilityByDateStringMap = getters.weeklyAvailabilityByDateStringMap[weeklyAvailabilityUid]
      const isAmPmMode = rootGetters["accountModule/isAmPmTimeFormat"]
      const timeZone = rootGetters["calendarSettingsModule/selectedStaff"].time_zone_name  || rootGetters["accountModule/staffTimeZoneName"];

      let res = getters.dateStringsArray.map(dateString => {
        const originalWeeklySlots = JSON.parse(JSON.stringify(weeklyAvailabilityByDateStringMap[dateString].slots))
        modifySlotsToCorrectAmPmMode(originalWeeklySlots, isAmPmMode)
        let dateSlot = {
          ignoreSlots: [],
          originalWeeklySlots,
          availabilityDate: moment(weeklyAvailabilityByDateStringMap[dateString].availabilityDate),
          slots: JSON.parse(JSON.stringify(weeklyAvailabilityByDateStringMap[dateString].slots)),
          isEdited: weeklyAvailabilityByDateStringMap[dateString].isEdited,
        }
        if (weeklyException) {
          modifySlotsWithExceptions(dateSlot, weeklyException, dateString, timeZone)
        }

        dateSlot.slots = dateSlot.slots.sort((a, b) => {
          return a.start_time < b.start_time ? -1 : 1
        })

        modifySlotsToCorrectAmPmMode(dateSlot.slots, isAmPmMode);
        return dateSlot
      })
      data[weeklyAvailabilityUid] = res
    }
    return data;
  },
  getAvailabilitiesWithExceptions: (state, getters) => {
    const defaultWeeklyAvailabilityUid = getters.weeklyAvailabilityUids[0]
    const availabilitiesWithExceptions = getters.availabilitiesWithExceptions
    return (weeklyAvailabilityUid = defaultWeeklyAvailabilityUid) => {
      return availabilitiesWithExceptions[weeklyAvailabilityUid]
    }
  },
  combinedAvailabilityMap: (state, getters, rootState, rootGetters) => {
    const data = {}
    for (const weeklyAvailabilityUid of state.currWeeklyAvailabilityUids) {
      const slotsMap = getSlotsMapFromCache(weeklyAvailabilityUid);
      const availabilitiesWithExceptions = getters.availabilitiesWithExceptions[weeklyAvailabilityUid]
      if (availabilitiesWithExceptions) {
        const calendarInterval = rootGetters["accountModule/calendarInterval"];
        const calendarIntervalMS = calendarInterval * MS_PER_MINUTE;
        availabilitiesWithExceptions.forEach(day => {
          const mergedSlots = mergeSlots(day.slots);

          let dayStartMS = day.availabilityDate.valueOf()
          const cachedDay = slotsMap[dayStartMS]
          if (cachedDay) {
            const cachedSlotsKeysArr = Object.keys(cachedDay).sort()
            const mergedSlotsKeysArr = mergedSlots.map((currSlot) => {
              let {startTimeMS, endTimeMS} = getSlotStartAndEndTimes(dayStartMS, currSlot, calendarIntervalMS);
              return `${startTimeMS}-${endTimeMS}-${calendarIntervalMS}`;
            }).sort();
            const shouldRecalculate = JSON.stringify(cachedSlotsKeysArr) !== JSON.stringify(mergedSlotsKeysArr);
            if (!shouldRecalculate) {
              return
            }
          }
          slotsMap[dayStartMS] = {}
          for (const slot of mergedSlots) {
            let {startTimeMS, endTimeMS} = getSlotStartAndEndTimes(dayStartMS, slot, calendarIntervalMS);
            if (slot.start_time_24 === '00:00' && slot.end_time_24 === '00:00') {
              // handles all day availability
              endTimeMS += MS_PER_DAY - 1;
            }
            if(endTimeMS < startTimeMS) { //edge case happens only when endTimeMS is taken from 00:00
              endTimeMS += MS_PER_DAY - 1;
            }
            const cachedMapKey = `${startTimeMS}-${endTimeMS}-${calendarIntervalMS}`;
            const cachedMap = slotsMap[dayStartMS][cachedMapKey] = {};

            iterateBetweenDates(startTimeMS, endTimeMS, (time) => {
              cachedMap[time] = true;
            }, calendarIntervalMS)
          }

          saveSlotsMapInCache(slotsMap, weeklyAvailabilityUid);
        });
      }
      data[weeklyAvailabilityUid] = slotsMapToMilliseconds(slotsMap)
    }
    return data;
  },
  getCombinedAvailabilityMap: (state, getters) => (weeklyAvailabilityUid = getters.weeklyAvailabilityUids[0]) => {
    return getters.combinedAvailabilityMap[weeklyAvailabilityUid];
  }
}


export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
